import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { Call4uAudioService, DtmfAction } from 'src/app/shared/services/call4u-audio.service';
import { Call4uAudio } from 'src/app/models/Call4uAudio';
import { LeadList } from 'src/app/models/LeadList';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';

interface DtmfActionRow {
  dtmf_key: string;
  action_type: string;
  action_text: string;
  action_list_id: string;
}

@Component({
  selector: 'app-call4u-audio-edit',
  templateUrl: './call4u-audio-edit.component.html',
  styleUrls: ['./call4u-audio-edit.component.scss']
})
export class Call4uAudioEditComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];

  audio: Call4uAudio | null = null;
  loading = true;

  // Campos editáveis
  title = '';
  text = '';

  // Player
  playerAudio: HTMLAudioElement | null = null;
  isPlayerPlaying = false;

  // Ações DTMF
  dtmfActions: DtmfActionRow[] = [];

  // Estado
  saving = false;

  // Listas do SMS Funnel
  lists: LeadList[] = [];

  // Opções de dígitos DTMF
  dtmfKeys = [
    { key: 'all', label: 'Todos (0-9, * e #)' },
    { key: '11', label: 'Não atendido' },
    { key: '10', label: 'Nenhuma opção' },
    { key: '0', label: '0' },
    { key: '1', label: '1' },
    { key: '2', label: '2' },
    { key: '3', label: '3' },
    { key: '4', label: '4' },
    { key: '5', label: '5' },
    { key: '6', label: '6' },
    { key: '7', label: '7' },
    { key: '8', label: '8' },
    { key: '9', label: '9' },
    { key: '12', label: '*' },
    { key: '13', label: '#' },
  ];

  private readonly allKeys = ['0','1','2','3','4','5','6','7','8','9','12','13'];

  // Opções de ações
  actionTypes = [
    { value: 'postback_sms_funnel', label: 'Postback' },
    { value: 'add_to_list', label: 'Lista' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private audioService: Call4uAudioService,
    private listsService: ListsService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.listsService.listAll().subscribe(lists => this.lists = lists)
    );
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadAudio(id);
    }
  }

  ngOnDestroy(): void {
    this.stopPlayer();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadAudio(id: string): void {
    this.loading = true;
    this.subscriptions.push(
      this.audioService.findById(id)
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: (audio) => {
            this.audio = audio;
            this.title = audio.title || '';
            this.text = audio.tts_text || '';
            this.dtmfActions = this.collapseActions(
              (audio.actions || []).map(a => ({
                dtmf_key: a.dtmf_key,
                action_type: a.action_type,
                action_text: this.extractActionText(a),
                action_list_id: this.extractListId(a),
              }))
            );
          },
          error: (err) => {
            this.toastService.error(err.error?.message || 'Erro ao carregar áudio');
            this.router.navigate(['/call4u-audios']);
          }
        })
    );
  }

  /**
   * Se todas as allKeys (0-9, *, #) estão presentes com mesmo action_type e params,
   * colapsa numa única linha com dtmf_key = 'all'.
   */
  private collapseActions(actions: DtmfActionRow[]): DtmfActionRow[] {
    const allKeySet = new Set(this.allKeys);
    const inAll = actions.filter(a => allKeySet.has(a.dtmf_key));
    const notInAll = actions.filter(a => !allKeySet.has(a.dtmf_key));

    if (inAll.length === this.allKeys.length) {
      const first = inAll[0];
      const sameType = inAll.every(a => a.action_type === first.action_type);
      const sameParams = inAll.every(a =>
        a.action_text === first.action_text && a.action_list_id === first.action_list_id
      );

      if (sameType && sameParams) {
        return [
          { dtmf_key: 'all', action_type: first.action_type, action_text: first.action_text, action_list_id: first.action_list_id },
          ...notInAll,
        ];
      }
    }

    return actions;
  }

  private extractActionText(action: any): string {
    if (!action.action_params) return '';
    const params = typeof action.action_params === 'string'
      ? JSON.parse(action.action_params)
      : action.action_params;
    return params.tag_id || params.sequence_id || params.webhook_url || params.text || params.url || '';
  }

  private extractListId(action: any): string {
    if (!action.action_params || action.action_type !== 'add_to_list') return '';
    const params = typeof action.action_params === 'string'
      ? JSON.parse(action.action_params)
      : action.action_params;
    return params.list_id || '';
  }

  // Player
  togglePlayer(): void {
    if (this.isPlayerPlaying) {
      this.stopPlayer();
    } else {
      this.playAudio();
    }
  }

  playAudio(): void {
    if (!this.audio?.file_url) {
      this.toastService.error('URL do áudio não disponível');
      return;
    }

    this.playerAudio = new Audio(this.audio.file_url);
    this.isPlayerPlaying = true;

    this.playerAudio.addEventListener('ended', () => {
      this.isPlayerPlaying = false;
      this.playerAudio = null;
    }, { once: true });

    this.playerAudio.play().catch(() => {
      this.toastService.error('Erro ao reproduzir áudio');
      this.isPlayerPlaying = false;
      this.playerAudio = null;
    });
  }

  stopPlayer(): void {
    if (this.playerAudio) {
      this.playerAudio.pause();
      this.playerAudio = null;
    }
    this.isPlayerPlaying = false;
  }

  // DTMF Actions
  addAction(): void {
    this.dtmfActions.push({ dtmf_key: '', action_type: '', action_text: '', action_list_id: '' });
  }

  removeAction(index: number): void {
    this.dtmfActions.splice(index, 1);
  }

  getAvailableKeys(currentIndex: number): { key: string; label: string }[] {
    const otherActions = this.dtmfActions.filter((_, i) => i !== currentIndex);
    const usedKeys = otherActions.map(a => a.dtmf_key);
    const allUsed = usedKeys.includes('all');

    return this.dtmfKeys.filter(k => {
      if (usedKeys.includes(k.key)) return false;
      if (allUsed && this.allKeys.includes(k.key)) return false;
      if (k.key === 'all' && usedKeys.some(uk => this.allKeys.includes(uk))) return false;
      return true;
    });
  }

  needsTextParam(actionType: string): boolean {
    return ['postback_sms_funnel'].includes(actionType);
  }

  needsListParam(actionType: string): boolean {
    return actionType === 'add_to_list';
  }

  getParamPlaceholder(actionType: string): string {
    const placeholders: Record<string, string> = {
      'postback_sms_funnel': 'Digite a url aqui',
    };
    return placeholders[actionType] || '';
  }

  getParamLabel(actionType: string): string {
    const labels: Record<string, string> = {
      'postback_sms_funnel': 'Url',
    };
    return labels[actionType] || 'Parâmetro';
  }

  // Validação
  get isFormValid(): boolean {
    if (!this.title.trim()) return false;
    for (const action of this.dtmfActions) {
      if (!action.dtmf_key || !action.action_type) return false;
      if (this.needsTextParam(action.action_type) && !action.action_text.trim()) return false;
      if (this.needsListParam(action.action_type) && !action.action_list_id) return false;
    }
    return true;
  }

  // Salvar
  save(): void {
    if (!this.isFormValid || this.saving || !this.audio) return;
    this.saving = true;

    const actions = this.buildActions();
    const data: any = { title: this.title, tts_text: this.text, actions };

    this.subscriptions.push(
      this.audioService.update(this.audio.id, data)
        .pipe(finalize(() => this.saving = false))
        .subscribe({
          next: () => {
            this.toastService.success('Áudio atualizado com sucesso!');
            this.router.navigate(['/call4u-audios']);
          },
          error: (err) => this.toastService.error(err.error?.message || 'Erro ao atualizar áudio')
        })
    );
  }

  private buildActions(): DtmfAction[] {
    const result: DtmfAction[] = [];

    for (const a of this.dtmfActions) {
      const keys = a.dtmf_key === 'all' ? this.allKeys : [a.dtmf_key];

      for (const key of keys) {
        const action: DtmfAction = { dtmf_key: key, action_type: a.action_type };

        if (this.needsTextParam(a.action_type) && a.action_text) {
          const paramKey = this.getParamKey(a.action_type);
          action.action_params = { [paramKey]: a.action_text };
        } else if (this.needsListParam(a.action_type) && a.action_list_id) {
          action.action_params = { list_id: a.action_list_id };
        }

        result.push(action);
      }
    }

    return result;
  }

  private getParamKey(actionType: string): string {
    const keys: Record<string, string> = {
      'postback_sms_funnel': 'url',
    };
    return keys[actionType] || 'value';
  }

  formatDuration(seconds: number): string {
    if (!seconds) return '--:--';
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  }

  getTypeLabel(type: string): string {
    return type === 'tts' ? 'TTS (IA)' : 'Upload';
  }
}
